// Avoid re-exports in favor of direct imports or entry-point files
/* eslint-disable no-barrel-files/no-barrel-files */
import { graphql } from 'react-relay';
import { useState, useContext, useEffect } from 'react';

import type { ApolloErrorInterop } from '@confluence/apollo-relay-interop-utils/entry-points/ApolloErrorInterop.ts';
import { useQueryInterop } from '@confluence/apollo-relay-interop/entry-points/useQueryInterop.ts';
import { useMutationInterop } from '@confluence/apollo-relay-interop/entry-points/useMutationInterop.ts';
import { useSessionData } from '@confluence/session-data/entry-points/useSessionData.ts';
import { getSingleParam } from '@confluence/route-manager-utils/entry-points/getSingleParam.ts';
import { RoutesContext } from '@confluence/route-manager/entry-points/RoutesContext.ts';
import { useWriteFragmentInterop } from '@confluence/apollo-relay-interop/entry-points/useWriteFragmentInterop.ts';

import type { useRecentPreferencesUserPreferencesFragment$data } from './__generated__/useRecentPreferencesUserPreferencesFragment.graphql.ts';
import useRecentPreferencesUserPreferencesFragmentArtifact from './__generated__/useRecentPreferencesUserPreferencesFragment.graphql.ts';
import { RecentFilter } from './__types__/useRecentPreferencesHomeRecentUserPreferencesMutation.ts';
import type {
	useRecentPreferencesHomeRecentUserPreferencesQuery$data,
	useRecentPreferencesHomeRecentUserPreferencesQuery$variables,
} from './__generated__/useRecentPreferencesHomeRecentUserPreferencesQuery.graphql.ts';
import type {
	useRecentPreferencesHomeRecentUserPreferencesMutation$data,
	useRecentPreferencesHomeRecentUserPreferencesMutation$variables,
} from './__generated__/useRecentPreferencesHomeRecentUserPreferencesMutation.graphql.ts';

type UseRecentPreferences = (defaultPreference?: RecentFilter) => {
	loading: boolean;
	queryError?: ApolloErrorInterop;
	mutationError?: ApolloErrorInterop;
	filter: RecentFilter;
	updateFilter: (filter: RecentFilter) => void;
};
type UseRecentPreferencesOverride = () => {
	filterOverride?: RecentFilter;
	clearFilterOverride: () => void;
};

const UseRecentPreferencesHomeRecentUserPreferencesQuery = graphql`
	query useRecentPreferencesHomeRecentUserPreferencesQuery {
		userPreferences {
			recentFilter
		}
	}
`;

const UseRecentPreferencesHomeRecentUserPreferencesMutation = graphql`
	mutation useRecentPreferencesHomeRecentUserPreferencesMutation($filter: RecentFilter) {
		updateUserPreferences(userPreferences: { recentFilter: $filter }) {
			recentFilter
		}
	}
`;

export const useRecentPreferences: UseRecentPreferences = (
	defaultPreference = RecentFilter.ALL,
) => {
	const { isLoggedIn } = useSessionData();
	const writeFragmentInterop =
		useWriteFragmentInterop<useRecentPreferencesUserPreferencesFragment$data>({
			fragmentArtifact: useRecentPreferencesUserPreferencesFragmentArtifact,
			fragmentSpec: graphql`
				fragment useRecentPreferencesUserPreferencesFragment on UserPreferences {
					recentFilter
				}
			`,
		});
	const {
		data,
		loading,
		error: queryError,
	} = useQueryInterop<
		useRecentPreferencesHomeRecentUserPreferencesQuery$data,
		useRecentPreferencesHomeRecentUserPreferencesQuery$variables
	>(UseRecentPreferencesHomeRecentUserPreferencesQuery, {
		skip: !isLoggedIn,
	});
	const filter = (data?.userPreferences?.recentFilter as RecentFilter) || defaultPreference;
	const [homeRecentUserPreferencesMutation, { error: mutationError }] = useMutationInterop<
		useRecentPreferencesHomeRecentUserPreferencesMutation$data,
		useRecentPreferencesHomeRecentUserPreferencesMutation$variables
	>(UseRecentPreferencesHomeRecentUserPreferencesMutation);
	const updateFilter = (updatedFilter: RecentFilter) => {
		if (isLoggedIn) {
			void homeRecentUserPreferencesMutation({
				variables: { filter: updatedFilter },
				optimisticResponse: {
					// @ts-ignore __typename does not exist on autogenerated graphql types
					__typename: 'Mutation',
					updateUserPreferences: {
						// @ts-ignore __typename does not exist on autogenerated graphql types
						__typename: 'UserPreferences',
						recentFilter: updatedFilter,
					},
				},
			}).catch(() => {}); // Ignore the error here since it is handled in useMutation
			// Always write to the cache so the user sees the preference even if there is an error
			writeFragmentInterop({
				fragmentId: '$ROOT_QUERY.userPreferences',
				data: {
					recentFilter: updatedFilter,
					__typename: 'UserPreferences',
				},
			});
		}
	};
	return {
		loading,
		queryError,
		mutationError,
		filter,
		updateFilter,
	};
};
export const useAnonymousRecentPreference: UseRecentPreferences = (
	defaultPreference = RecentFilter.ALL,
) => {
	const [filter, setFilter] = useState(defaultPreference);
	return {
		loading: false,
		filter,
		updateFilter: setFilter,
	};
};
export const useRecentPreferencesOverride: UseRecentPreferencesOverride = () => {
	const { getQueryParams, setQueryParams, history } = useContext(RoutesContext);
	const filterQueryParam = getSingleParam(getQueryParams(), 'filter') as RecentFilter | undefined;
	const filter =
		filterQueryParam && Object.values(RecentFilter).includes(filterQueryParam)
			? filterQueryParam
			: undefined;
	const [filterOverride, setFilterOverride] = useState<RecentFilter | undefined>(filter);
	useEffect(() => {
		setQueryParams({ filter: undefined });
		return history?.listen(() => {
			const updatedFilterQueryParam = getSingleParam(getQueryParams(), 'filter') as
				| RecentFilter
				| undefined;
			if (updatedFilterQueryParam) {
				const updatedFilter = Object.values(RecentFilter).includes(updatedFilterQueryParam)
					? updatedFilterQueryParam
					: undefined;
				setFilterOverride(updatedFilter);
				setQueryParams({ filter: undefined });
			}
		});
	}, [setQueryParams, getQueryParams, history]);
	const clearFilterOverride = () => {
		setFilterOverride(undefined);
	};
	return {
		filterOverride,
		clearFilterOverride,
	};
};
export { RecentFilter } from './__types__/useRecentPreferencesHomeRecentUserPreferencesMutation.ts';
